import {DEFAULT_VALUES} from '@packages/systems/style-legacy/media-queries';
import {
  BREAKPOINT_ID_MAIN,
  BREAKPOINT_ID_TINY,
  BREAKPOINT_ID_SMALL,
  BREAKPOINT_ID_MEDIUM,
  BREAKPOINT_ID_LARGE,
  BREAKPOINT_ID_XL,
  BREAKPOINT_ID_XXL,
} from '@packages/systems/style/breakpoint-ids';
import type {BreakpointID, BreakpointsMap} from '@packages/systems/style/types';
import type {Breakpoint} from '@packages/systems/style/media-queries';
import {getBreakpointVisualRange} from '@packages/systems/ui/canvas';

const BREAKPOINT_ID_MAIN_WIDTH = 1136;

export const DEFAULT_WIDTH_OVERRIDES: Readonly<
  Partial<Record<BreakpointID, number>>
> = {
  [BREAKPOINT_ID_TINY]: 393,
  [BREAKPOINT_ID_SMALL]: 667,
  [BREAKPOINT_ID_MEDIUM]: 820,
};

/**
 * Verified CSS viewport defaults for canonical devices (same devices as
 * modern width markers where applicable). Used as the Open Canvas / MCC
 * `vh`/`svh`/`dvh`/`lvh` base (`--__wf_oc_vh-base`) and as the reset
 * tooltip device name.
 *
 * - tiny: iPhone 16 (393×852)
 * - small: iPhone SE 3rd gen landscape (667×375)
 * - medium: iPad (10th gen) (820×1180)
 * - main / large: MacBook Air 13" (M3) (1280×832) — Desktop (~1279) reuses this height
 * - xl: MacBook Air 15" (M3) (1440×932)
 * - xxl: Full HD monitor (1920×1080)
 */
const BREAKPOINT_DEFAULT_VIEWPORTS: Record<
  BreakpointID,
  {device: string; height: number}
> = {
  [BREAKPOINT_ID_TINY]: {device: 'iPhone 16', height: 852},
  [BREAKPOINT_ID_SMALL]: {
    device: 'iPhone SE 3rd gen landscape',
    height: 375,
  },
  [BREAKPOINT_ID_MEDIUM]: {device: 'iPad (10th gen)', height: 1180},
  [BREAKPOINT_ID_MAIN]: {device: 'MacBook Air 13" (M3)', height: 832},
  [BREAKPOINT_ID_LARGE]: {device: 'MacBook Air 13" (M3)', height: 832},
  [BREAKPOINT_ID_XL]: {device: 'MacBook Air 15" (M3)', height: 932},
  [BREAKPOINT_ID_XXL]: {device: 'Full HD monitor', height: 1080},
};

export const getBreakpointWidth = (breakpointId: BreakpointID): number => {
  if (breakpointId === BREAKPOINT_ID_MAIN) return BREAKPOINT_ID_MAIN_WIDTH;

  const defaultWidthOverride = DEFAULT_WIDTH_OVERRIDES[breakpointId];
  if (defaultWidthOverride != null) {
    return defaultWidthOverride;
  }

  // Use DEFAULT_VALUES for remaining breakpoints, fallback to main width
  return DEFAULT_VALUES[breakpointId] ?? BREAKPOINT_ID_MAIN_WIDTH;
};

export function getBreakpointHeight(breakpointId: BreakpointID): number {
  return BREAKPOINT_DEFAULT_VIEWPORTS[breakpointId].height;
}

/**
 * Menu item / tooltip copy when Viewport height is custom. Names the
 * verified default device for this breakpoint (sentence case; no period —
 * action label, not a full sentence).
 */
export function getBreakpointViewportHeightResetLabel(
  breakpointId: BreakpointID
): string {
  const {device} = BREAKPOINT_DEFAULT_VIEWPORTS[breakpointId];
  return `Reset to ${device} viewport height`;
}

/**
 * Returns the valid width range for a frame at the given breakpoint.
 * Falls back to the breakpoint's default width when the breakpoint
 * isn't found in the map.
 */
export const getBreakpointWidthRange = (
  breakpoints: BreakpointsMap<Breakpoint> | null,
  breakpointId: BreakpointID
): {min: number; max: number} => {
  const defaultWidth = getBreakpointWidth(breakpointId);
  const currentBreakpoint = breakpoints?.[breakpointId] ?? null;
  if (!currentBreakpoint) return {min: defaultWidth, max: defaultWidth};
  return getBreakpointVisualRange(breakpoints!, currentBreakpoint);
};
